import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from './Footer';
import ErrorBoundary from './ErrorBoundary';

const PublicLayout = ({ children, theme = 'light' }) => {
  // Always use elysian-modern theme
  const currentTheme = 'elysian-modern';
  const location = useLocation();
  const [isThemeLoaded, setIsThemeLoaded] = React.useState(false);
  const [isContentReady, setIsContentReady] = React.useState(false);
  
  // Load theme stylesheet immediately
  React.useEffect(() => {
    // Reset ready state on location change
    setIsContentReady(false);
    
    // Check if theme is already loaded
    const existingLink = document.getElementById('public-theme-stylesheet');
    if (existingLink) {
      setIsThemeLoaded(true);
      // Small delay to ensure DOM is ready
      setTimeout(() => setIsContentReady(true), 50);
      return;
    }
    
    // Create and load theme stylesheet
    const link = document.createElement('link');
    link.id = 'public-theme-stylesheet';
    link.rel = 'stylesheet';
    link.href = `/themes/${currentTheme}.css`;
    
    // Set loaded state when stylesheet loads
    link.onload = () => {
      setIsThemeLoaded(true);
      setTimeout(() => setIsContentReady(true), 50);
    };
    
    document.head.appendChild(link);
    
    // Fallback - mark as loaded after short delay even if onload doesn't fire
    const timer = setTimeout(() => {
      setIsThemeLoaded(true);
      setIsContentReady(true);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [currentTheme, location.pathname]);
  
  // Don't render content until theme is loaded and ready
  if (!isThemeLoaded || !isContentReady) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: '#FDFBF7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #F7F5F0',
          borderTop: '3px solid #D4AF37',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  return (
    <ErrorBoundary suppressErrors={true} location={location.pathname}>
      <div className={`theme-${currentTheme} min-h-screen flex flex-col`}>
      {/* Header */}
      <header style={{
        backgroundColor: '#000',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-14 h-14 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_refined-login/artifacts/7hacqtlv_Success%20Club%20Logo%20Files%20-%203%20%281%29.png" 
                  alt="Success Club Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold" style={{color: '#fff', fontFamily: 'var(--font-heading)'}}>SUCCESS CLUB</span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/features" className="transition-colors" style={{color: 'rgba(255,255,255,0.8)', fontWeight: 500}}>
                Features
              </Link>
              <Link to="/events" className="transition-colors" style={{color: 'rgba(255,255,255,0.8)', fontWeight: 500}}>
                Events
              </Link>
              <Link to="/magazine" className="transition-colors" style={{color: 'rgba(255,255,255,0.8)', fontWeight: 500}}>
                Magazine
              </Link>
              <Link to="/auth" className="btn-primary no-underline" style={{padding: '0.75rem 1.5rem'}}>
                Login
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer />
      </div>
    </ErrorBoundary>
  );
};

export default PublicLayout;